<template>
  <div>
    <v-tabs v-model="activeTab" grow slider-color="primary" slider-size="3">
      <v-tab key="groups">
        <v-icon left size="20">mdi-folder-multiple</v-icon>
        Groups
      </v-tab>
      <v-tab key="branches">
        <v-icon left size="20">mdi-source-branch</v-icon>
        Branches
      </v-tab>
    </v-tabs>

    <v-divider></v-divider>

    <v-tabs-items v-model="activeTab">
      <!-- Groups Tab -->
      <v-tab-item key="groups" transition="fade-transition" reverse-transition="fade-transition">
        <v-card flat class="pa-4">
          <v-overlay v-if="loading" absolute>
            <v-progress-circular indeterminate color="primary"/>
          </v-overlay>

          <div class="d-flex align-center mb-4">
            <v-chip small color="primary" outlined>{{ allGroups.length }} groups</v-chip>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="selectedGroupsForDeletion.length === 0 || !ifCanEditConfigTag()"
              color="error"
              small
              class="mr-2"
              @click="bulkRemoveGroups">
              <v-icon left small>mdi-delete</v-icon>
              Remove Selected ({{ selectedGroupsForDeletion.length }})
            </v-btn>
            <v-btn
              :disabled="!ifCanEditConfigTag()"
              color="primary"
              small
              @click="openAddGroupDialog">
              <v-icon left small>mdi-plus</v-icon>
              Add Group
            </v-btn>
          </div>

          <v-card outlined class="rounded-lg">
            <v-list dense>
              <v-list-item-group v-model="selectedGroupsForDeletion" multiple>
                <v-list-item
                  v-for="groupInfo in allGroups"
                  :key="groupInfo.uniqueKey"
                  :value="groupInfo"
                  :disabled="!ifCanEditConfigTag()">
                  <template v-slot:default="{ active }">
                    <v-list-item-action>
                      <v-checkbox :input-value="active" color="primary"></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-chip
                          class="mr-2"
                          color="blue lighten-4"
                          text-color="blue--text"
                          x-small>
                          {{ groupInfo.branch.name }}
                        </v-chip>
                        <v-chip
                          color="orange lighten-4"
                          text-color="orange--text"
                          x-small>
                          {{ groupInfo.group.name }}
                        </v-chip>
                        <v-chip
                          class="ml-2"
                          :color="groupInfo.configsCount > 0 ? 'green lighten-4' : 'grey lighten-3'"
                          :text-color="groupInfo.configsCount > 0 ? 'green--text' : 'grey--text'"
                          x-small>
                          {{ groupInfo.configsCount }} configs
                        </v-chip>
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn
                        :disabled="!ifCanEditConfigTag()"
                        icon
                        x-small
                        color="error"
                        @click.stop="removeSingleGroup(groupInfo)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </template>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-card>

          <!-- Add Group Dialog -->
          <v-dialog v-model="addGroupDialog" max-width="500px">
            <v-card>
              <v-card-title>
                <span class="headline">Add Group to Branch</span>
              </v-card-title>
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12">
                      <v-select
                        v-model="selectedBranchForNewGroup"
                        :items="branches"
                        item-text="name"
                        item-value="uid"
                        label="Select Branch"
                        outlined
                        return-object
                        dense>
                      </v-select>
                    </v-col>
                    <v-col cols="12" v-if="orphanedGroupNames.length > 0">
                      <div class="caption grey--text mb-1">Unassigned groups (click to use):</div>
                      <v-chip
                        v-for="name in orphanedGroupNames"
                        :key="name"
                        small
                        class="mr-1 mb-1"
                        color="orange lighten-4"
                        text-color="orange--text"
                        @click="newGroupName = name">
                        {{ name }}
                      </v-chip>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="newGroupName"
                        label="Group Name"
                        outlined
                        dense
                        v-ascii-only>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="closeAddGroupDialog">Cancel</v-btn>
                <v-btn color="blue darken-1" text @click="saveNewGroup">Add</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card>
      </v-tab-item>

      <!-- Branches Tab -->
      <v-tab-item key="branches" transition="fade-transition" reverse-transition="fade-transition">
        <v-card flat class="pa-4">
          <c-branch-table></c-branch-table>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import {ConfigStoreUtils} from "@/modules/mconfigs/database/ConfigStoreUtils";
import {PermissionsStoreUtils} from "@/modules/mpermissions/database/PermissionsStoreUtils";
import BranchStoreUtils from "@/modules/mconfigs/database/BranchStoreUtils";
import {GroupModel} from "@/modules/mconfigs/models/GroupModel";
import {ConfigModel} from "@/modules/mconfigs/models/ConfigModel";
import CBranchTable from "@/modules/mconfigs/views/branch/c-branch-table.vue";

export default {
  name: "c-pre-release-management",
  components: {CBranchTable},
  data: () => ({
    activeTab: null,
    loading: null,
    selectedGroupsForDeletion: [],
    addGroupDialog: false,
    selectedBranchForNewGroup: null,
    newGroupName: '',
  }),
  methods: {
    getMyUserUid() {
      return PermissionsStoreUtils.getMyUserUid();
    },
    ifCanEditConfigTag() {
      return PermissionsStoreUtils.getPermission(this.getMyUserUid(), PermissionsStoreUtils.CONFIG_TAB.subpermissions.CONFIG_TAB_PRE_EDIT_TAG.id);
    },
    openAddGroupDialog() {
      this.selectedBranchForNewGroup = this.branches.length > 0 ? this.branches[0] : null;
      this.newGroupName = '';
      this.addGroupDialog = true;
    },
    closeAddGroupDialog() {
      this.addGroupDialog = false;
      this.selectedBranchForNewGroup = null;
      this.newGroupName = '';
    },
    saveNewGroup() {
      if (!this.selectedBranchForNewGroup || !this.newGroupName || this.newGroupName.trim() === '') {
        this.$notify({title: 'Validation Error', text: 'Branch and Group Name are required', type: "error"});
        return;
      }

      const existingGroup = this.selectedBranchForNewGroup.groups.find(g => g.name?.toLowerCase() === this.newGroupName.trim().toLowerCase());
      if (existingGroup) {
        this.$notify({title: 'Validation Error', text: 'Group already exists in this branch', type: "error"});
        return;
      }

      const allBranches = BranchStoreUtils.getValues();
      const duplicateInOtherBranch = allBranches.some(b =>
          b.uid !== this.selectedBranchForNewGroup.uid &&
          b.groups.some(g => g.name?.toLowerCase() === this.newGroupName.trim().toLowerCase())
      );

      if (duplicateInOtherBranch) {
        this.$notify({title: 'Validation Error', text: 'Group name already exists in another branch', type: "error"});
        return;
      }

      const newGroup = new GroupModel();
      newGroup.name = this.newGroupName.trim();

      const branchCopy = this.selectedBranchForNewGroup.clone();
      branchCopy.groups.push(newGroup);

      const existingConfigs = ConfigStoreUtils.getReleaseConfigs()
          .filter(c => c.group?.toLowerCase() === newGroup.name.toLowerCase());
      const isOrphanedGroup = existingConfigs.length > 0;

      this.loading = true;
      BranchStoreUtils.pushValue(branchCopy)
          .then(() => {
            if (!isOrphanedGroup) {
              const newConfig = new ConfigModel(newGroup.name, 0);
              newConfig.tag = newGroup.name;
              return ConfigStoreUtils.updateReleaseConfig(newConfig, null);
            }
          })
          .then(() => {
            this.$notify({title: 'Group successfully added!', text: "", type: "success"});
            this.closeAddGroupDialog();
          })
          .catch((error) => {
            this.$notify({title: 'Group addition failed!', text: error, type: "error"});
          })
          .finally(() => {
            this.loading = null;
          });
    },
    removeSingleGroup(groupInfo) {
      const configsInGroup = ConfigStoreUtils.getReleaseConfigs().filter(c => c.group?.toLowerCase() === groupInfo.group.name?.toLowerCase());

      const message = configsInGroup.length > 0
          ? `Are you sure you want to remove group "${groupInfo.group.name}" from branch "${groupInfo.branch.name}"? ${configsInGroup.length} config(s) will be deleted.`
          : `Are you sure you want to remove group "${groupInfo.group.name}" from branch "${groupInfo.branch.name}"?`;

      this.$confirm(message).then(() => {
        this.removeGroupFromBranch(groupInfo.branch, groupInfo.group);
      });
    },
    bulkRemoveGroups() {
      if (this.selectedGroupsForDeletion.length === 0) {
        return;
      }

      const totalConfigs = this.selectedGroupsForDeletion.reduce((sum, groupInfo) => {
        return sum + ConfigStoreUtils.getReleaseConfigs().filter(c => c.group?.toLowerCase() === groupInfo.group.name?.toLowerCase()).length;
      }, 0);

      const message = totalConfigs > 0
          ? `Are you sure you want to remove ${this.selectedGroupsForDeletion.length} group(s) from their branches? ${totalConfigs} config(s) will be deleted.`
          : `Are you sure you want to remove ${this.selectedGroupsForDeletion.length} group(s) from their branches?`;

      this.$confirm(message).then(() => {
        this.loading = true;

        const removalsByBranch = new Map();
        this.selectedGroupsForDeletion.forEach(groupInfo => {
          if (!removalsByBranch.has(groupInfo.branch.uid)) {
            removalsByBranch.set(groupInfo.branch.uid, {
              branch: groupInfo.branch,
              groupsToRemove: []
            });
          }
          removalsByBranch.get(groupInfo.branch.uid).groupsToRemove.push(groupInfo.group);
        });

        const branchUpdates = [];
        const configRemovals = [];
        removalsByBranch.forEach(({branch, groupsToRemove}) => {
          const branchCopy = branch.clone();
          branchCopy.groups = branchCopy.groups.filter(g =>
              !groupsToRemove.some(gr => gr.name?.toLowerCase() === g.name?.toLowerCase())
          );

          branchUpdates.push(BranchStoreUtils.pushValue(branchCopy));

          groupsToRemove.forEach(group => {
            const configsInGroup = ConfigStoreUtils.getReleaseConfigs().filter(c => c.group?.toLowerCase() === group.name?.toLowerCase());
            configsInGroup.forEach(c => configRemovals.push(ConfigStoreUtils.removeReleaseConfig(c.uid)));
          });
        });

        Promise.all([...branchUpdates, ...configRemovals])
            .then(() => {
              this.$notify({title: `Successfully removed ${this.selectedGroupsForDeletion.length} group(s)!`, text: "", type: "success"});
              this.selectedGroupsForDeletion = [];
            })
            .catch((error) => {
              this.$notify({title: 'Group removal failed!', text: error, type: "error"});
            })
            .finally(() => {
              this.loading = null;
            });
      });
    },
    removeGroupFromBranch(branch, group) {
      this.loading = true;

      const branchCopy = branch.clone();
      branchCopy.groups = branchCopy.groups.filter(g => g.name?.toLowerCase() !== group.name?.toLowerCase());

      const configsInGroup = ConfigStoreUtils.getReleaseConfigs().filter(c => c.group?.toLowerCase() === group.name?.toLowerCase());
      const configRemovals = configsInGroup.map(c => ConfigStoreUtils.removeReleaseConfig(c.uid));

      Promise.all([BranchStoreUtils.pushValue(branchCopy), ...configRemovals])
          .then(() => {
            this.$notify({title: 'Group successfully removed from branch!', text: "", type: "success"});
          })
          .catch((error) => {
            this.$notify({title: 'Group removal failed!', text: error, type: "error"});
          })
          .finally(() => {
            this.loading = null;
          });
    },
  },
  computed: {
    branches() {
      return BranchStoreUtils.getValues() || [];
    },
    allGroups() {
      const branches = this.branches;
      const configs = ConfigStoreUtils.getReleaseConfigs();
      const result = [];

      branches.forEach(branch => {
        branch.groups.forEach(group => {
          const configsCount = configs.filter(c => c.group?.toLowerCase() === group.name?.toLowerCase()).length;
          result.push({
            branch: branch,
            group: group,
            configsCount: configsCount,
            uniqueKey: `${branch.uid}_${group.name}`
          });
        });
      });

      return result;
    },
    orphanedGroupNames() {
      const branches = this.branches;
      const configs = ConfigStoreUtils.getReleaseConfigs();
      const assignedGroupNames = new Set(
          branches.flatMap(b => b.groups.map(g => g.name?.toLowerCase())).filter(Boolean)
      );
      return [...new Set(
          configs
              .map(c => c.group)
              .filter(g => g && !assignedGroupNames.has(g.toLowerCase()))
      )];
    },
  }
}
</script>

<style scoped>
</style>
